import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useI18n } from '@/i18n/I18nProvider'
import { localePath } from '@/app/routes'
import { navLocale } from '@/i18n/href'
import { normalisePath, probeApplies } from '@/lib/localeProbe'
import { sameSitePath, track } from '@/lib/track'

/**
 * Rendered for any path that is not a real (locale, page) pair, and prerendered
 * once to `dist/client/404.html` for the Worker's assets binding to serve.
 *
 * Not translated through the dictionary: this page has to render when the locale
 * itself is the thing that was wrong, so it says its two words in the base
 * language and offers the way back rather than pretending to know who is reading.
 */

/**
 * The path this DOCUMENT was served for, read once when the bundle evaluates —
 * before hydration and before any client-side navigation can move it.
 *
 * It is what separates the two ways a reader reaches this page, and the naive
 * version of that test is wrong: "is this the first time NotFound has mounted"
 * calls a dead in-app link clicked from a real page a server 404, because it is
 * the first mount in that page's life. Comparing against the entry URL cannot
 * make that mistake.
 *
 * Guarded because this module is also evaluated in Node during the prerender,
 * where there is no `location` — the value is never used there, since effects
 * do not run server-side.
 */
const ENTRY_PATH = typeof location === 'undefined' ? '' : location.pathname

export default function NotFound() {
  const { locale, t } = useI18n()
  const { pathname } = useLocation()

  // ---- BLOK 4's measurement half ----------------------------------------
  //
  // Two dead addresses were found by hand on 2026-09-14, both of them costing
  // real traffic — a gated e-book behind a live LinkedIn post, and a blog URL
  // still linked from off-site. Finding them took somebody noticing. This
  // event is the systematic version: every address that reaches a 404 says so,
  // with enough beside it to decide what to do about it.
  //
  // The rescue half of BLOK 4 is deliberately NOT here. It arrived as a
  // proposal for a client-side HEAD-probe-then-navigate and shipped instead as
  // a server-side 301 in the Worker, which carries the SEO signal a client-side
  // navigation does not and costs the visitor no round trip. So by the time
  // this component renders, the rescue has already been tried and failed.
  useEffect(() => {
    // `page_path` is already on every event `dp.track` sends, so the dead
    // address is not repeated here under a second name. One number, one place.
    track('page_not_found', {
      // `load` is a real 404 from the Worker — a bad link somewhere out in the
      // world, or a page we removed. `spa` is an in-app <Link> pointing at a
      // route that does not exist, which is our own bug and a different fix.
      nav: pathname === ENTRY_PATH ? 'load' : 'spa',

      // Where they came from, but ONLY as a path and ONLY when it is ours.
      //
      // Never the full referrer URL: a referrer carries whatever query string
      // the other site put on it — a search term, a session token, an email
      // address in a tracking parameter — and none of that has any business
      // being shipped to a third-party processor because somebody hit a 404.
      // The external case is not lost by this: PostHog sets
      // `$referring_domain` on every event by itself, which is the part worth
      // having (LinkedIn vs Google vs direct) without the payload.
      came_from: sameSitePath(document.referrer, location.origin),

      // Whether the Worker's `/en{path}` rescue was even eligible to run —
      // `probeApplies()`, the same function the Worker gates on, so the two
      // answers cannot drift.
      //
      // `true` means the probe looked for an English page at this address and
      // there was not one: the fix is a redirect row, and this is the pile to
      // work through. `false` means the shape was never a candidate (already
      // locale-prefixed, or an asset), so a redirect row would not have helped
      // and the dead link points somewhere else entirely.
      probe_eligible: probeApplies(normalisePath(pathname)),
    })
    // Keyed on the path: one mounted NotFound can serve two different dead
    // routes in a row, and the second one is its own finding.
  }, [pathname])

  return (
    <div className="site-container flex min-h-dvh flex-col items-center justify-center gap-6 py-24 text-center">
      <img src="/digitopia-logo.png" alt={t.site.name} className="h-8 w-auto" />
      <p className="font-mono text-sm text-muted-foreground">404</p>
      <h1 className="text-2xl font-extrabold tracking-tight">Page not found</h1>
      <Link
        to={localePath(navLocale(locale), '')}
        className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
      >
        {t.pages.home.navLabel}
      </Link>
    </div>
  )
}
